import React from 'react'
import './InsurancePlanStyle.css'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@enamelai/design-system'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { buildPatientProfilePreviousPage } from '@utils/patientProfileNavigation.util'
import ProfileStatusBadge from '@pages/PatientProfile/shared/ProfileStatusBadge.component'
import NetworkPlans from './NetworkPlans.component'

export interface Money {
  amount: number
  remaining: number
  used: number
}

export interface ServiceOutput {
  category: string
  deductibles?: {
    individual?: Money
    family?: Money
  }
  maximum?: {
    individual?: Money
    family?: Money
  }
}

export interface NetworkOutput {
  network: string
  networkType: number
  services: ServiceOutput[]
}

export interface InsurancePlanViewModel {
  id: string
  payerName: string
  subscriberId: string
  validity: string
  status: string
  networks: NetworkOutput[]
  onViewCoverage?: () => void
}

interface InsurancePlanCardProps {
  plan: InsurancePlanViewModel
}

export const InsurancePlanCard = ({
  plan,
}: InsurancePlanCardProps): JSX.Element | null => {
  const navigate = useNavigate()
  const { patientId = '' } = useParams<{ patientId: string }>()

  const handleViewCoverage = (planId: string): void => {
    navigate('/insurance', {
      state: {
        tabKey: 'coverage',
        activeInsurancePlanId: planId,
        ...(patientId
          ? {
              previousPage: buildPatientProfilePreviousPage(
                patientId,
                'insurance',
              ),
            }
          : {}),
      },
    })
  }

  if (plan.networks.length === 0) return null

  return (
    <article className="insurance-plan-card">
      <header className="insurance-plan-header">
        <div className="insurance-plan-header-row">
          <h2 className="insurance-plan-payer-name">{plan.payerName}</h2>
          <Button
            variant="primary"
            size="md"
            rightIcon={
              <ArrowRightIcon aria-hidden="true" width={16} height={16} />
            }
            onClick={(): void => handleViewCoverage(plan.id)}
          >
            View coverage
          </Button>
        </div>
        <div className="insurance-plan-meta-bar">
          <div className="insurance-plan-meta-item">
            <span className="insurance-plan-meta-label">Subscriber id:</span>
            <span className="insurance-plan-meta-value">
              {plan.subscriberId}
            </span>
          </div>
          <div className="insurance-plan-meta-item">
            <span className="insurance-plan-meta-label">Validity:</span>
            <span className="insurance-plan-meta-value">{plan.validity}</span>
          </div>
          <div className="insurance-plan-meta-item">
            <span className="insurance-plan-meta-label">Status:</span>
            <ProfileStatusBadge status={plan.status} />
          </div>
        </div>
      </header>

      <section className="insurance-plan-section">
        <NetworkPlans data={plan.networks} />
      </section>
    </article>
  )
}
